import React from 'react';
import { useNavigate } from 'react-router-dom';

// Friendly UI for entitlement walls — rendered in place of a page's data (locked), as an inline
// banner near an action (limit), or as the MLS connect prompt (no_market). Shared so every page
// degrades consistently instead of crashing on a 402/403 body.

const FEATURE_LABELS = {
  buyer_database:    'Buyers Database',
  underwriting:      'AI Underwriting',
  mls_skimmer:       'MLS Opportunities',
  email:             'Email outreach',
  sms:               'SMS outreach',
  offers_per_month:  'Offer generation',
  voicemail:         'Voicemail drops',
  ai_acq_agent:      'AI Acquisitions Agent',
  packet_generation: 'Deal Packets',
  deals_in_pipeline: 'Pipeline capacity',
};
export const featureLabel = (f) => FEATURE_LABELS[f] || 'This feature';

const LockIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// Full-panel "feature not on your plan" state — use in place of a page's primary content.
export function LockedFeaturePanel({ feature, plan }) {
  const navigate = useNavigate();
  const label = featureLabel(feature);
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, minHeight: 320 }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, margin: '0 auto 18px', borderRadius: 14, background: 'var(--teal-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <LockIcon />
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>{label} isn't included in your plan</div>
        <div style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.5, marginBottom: 22 }}>
          {plan ? <>Your <strong style={{ color: 'var(--gray-100, #fff)' }}>{plan}</strong> plan doesn't include {label.toLowerCase()}. </> : null}
          Upgrade to unlock it for your organization.
        </div>
        <button
          onClick={() => navigate('/settings')}
          style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid var(--teal)', background: 'var(--teal-dim)', color: 'var(--teal)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Upgrade plan
        </button>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 12 }}>Plan upgrades are coming soon.</div>
      </div>
    </div>
  );
}

// Inline banner for an action that hit a monthly cap (e.g. a send/generate). When the over-limit
// request is pending admin approval (Phase 1.5 flow), say so.
export function LimitReachedNotice({ error, style }) {
  const pending = error?.overLimit?.status === 'pending';
  const label = featureLabel(error?.feature);
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: 'var(--gold-dim, rgba(224, 165, 38,0.12))', border: '1px solid rgba(224, 165, 38,0.35)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--white)', ...style }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E0A526" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
      <div>
        <div style={{ fontWeight: 600 }}>You've reached your plan limit for {label.toLowerCase()}{error?.limit != null ? ` (${error.limit}/mo)` : ''}.</div>
        <div style={{ color: 'var(--gray-300)', marginTop: 2 }}>
          {pending
            ? 'Your request has been sent to an admin for approval — you\'ll be notified when it\'s raised.'
            : 'Upgrade your plan or wait until next month to continue.'}
        </div>
      </div>
    </div>
  );
}

// "Connect your MLS market" prompt — shared by the Phase-5 empty state AND the no_market (403)
// response from scan/sync triggers. One component, used in both places.
export function ConnectMlsPanel() {
  const navigate = useNavigate();
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, minHeight: 320 }}>
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, margin: '0 auto 18px', borderRadius: 14, background: 'var(--teal-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
          </svg>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--white)', marginBottom: 8 }}>No MLS opportunities yet</div>
        <div style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.5, marginBottom: 22 }}>
          Connect your MLS feed in Settings, then run <b>Sync&nbsp;now</b> to scan your market. Scored
          Hot&nbsp;/&nbsp;Watch&nbsp;/&nbsp;Maybe opportunities appear here — isolated to your organization.
        </div>
        <button onClick={() => navigate('/settings')}
          style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Go to Settings
        </button>
      </div>
    </div>
  );
}
